import React, { useState, useRef, useEffect } from 'react';
import { triggerSlackSync, checkSyncStatus } from '../utils/slackSyncService';
import { format } from 'date-fns';

/**
 * SyncButtonWithTimestamp
 * Combines the sync button and last synced timestamp in a flex row.
 * Props:
 *   - organization: org object or id
 *   - dateRange: optional, for future use 
 *   - onSyncComplete: callback when sync completes
 */
const SyncButtonWithTimestamp = ({ organization, dateRange, onSyncComplete }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState('');
  const pollCountRef = useRef(0);
  const [syncSuccess, setSyncSuccess] = useState(false);

  // Fetch last sync status on mount or when org changes
  useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const status = await checkSyncStatus(organization);
        if (status.lastSynced) {
          setLastSynced(status.lastSynced);
        }
      } catch (err) {
        // Ignore error
      }
    };
    if (organization) {
      fetchSyncStatus();
    }
  }, [organization]);

  const handleSync = async () => {
    setIsSyncing(true);
    setError(null);
    setPollCount(0);
    setSyncStatus('Starting event processing...');
    setSyncSuccess(false);
    pollCountRef.current = 0;
    try {
      const initialStatus = await checkSyncStatus(organization);
      const preSyncTimestamp = initialStatus.lastSynced
        ? new Date(initialStatus.lastSynced).getTime()
        : 0;
      const initialReportCount = initialStatus.reportCount || 0;

      const result = await triggerSlackSync(organization);
      if (result.success) {
        setSyncStatus('Processing buffered events...');
        const pollInterval = setInterval(async () => {
          try {
            const status = await checkSyncStatus(organization);
            pollCountRef.current += 1;
            setPollCount(pollCountRef.current);
            if (pollCountRef.current <= 3) setSyncStatus('Processing events...');
            else if (pollCountRef.current <= 6) setSyncStatus('Running AI analysis...');
            else if (pollCountRef.current <= 9) setSyncStatus('Generating insights...');
            else setSyncStatus('Finalizing...');

            const currentTimestamp = status.lastSynced
              ? new Date(status.lastSynced).getTime()
              : 0;
            const reportUpdated = currentTimestamp > preSyncTimestamp;
            const newReportCreated = status.reportCount > initialReportCount;
            const firstReport = initialReportCount === 0 && status.hasReports;

            if (reportUpdated || newReportCreated || firstReport) {
              clearInterval(pollInterval);
              setLastSynced(status.lastSynced || new Date());
              setIsSyncing(false);
              setSyncStatus('Processing complete!');
              setSyncSuccess(true);
              if (onSyncComplete) onSyncComplete(status.lastSynced || new Date());
              return;
            }
            if (pollCountRef.current >= 24) {
              clearInterval(pollInterval);
              const finalStatus = await checkSyncStatus(organization);
              if (finalStatus.hasReports) {
                setLastSynced(finalStatus.lastSynced || new Date());
                setIsSyncing(false);
                setSyncStatus('Processing complete!');
                setSyncSuccess(true);
                if (onSyncComplete) onSyncComplete(finalStatus.lastSynced || new Date());
              } else {
                setError('Processing timed out. It may still be running on the server.');
                setIsSyncing(false);
                setSyncStatus('');
              }
              return;
            }
          } catch (err) {}
        }, 5000);
      } else {
        setError('Failed to start event processing');
        setIsSyncing(false);
        setSyncStatus('');
      }
    } catch (err) {
      setError(err.message || 'Error processing events');
      setIsSyncing(false);
      setSyncStatus('');
    }
  };

  // Format timestamp as 'Last synced on Jul 4, 2025 at 4:00 PM'
  const getFormattedTimestamp = () => {
    if (!lastSynced) return '';
    try {
      const date = new Date(lastSynced);
      return `Last synced on ${format(date, 'MMM d, yyyy')} at ${format(date, 'h:mm a')}`;
    } catch {
      return '';
    }
  };

  return (
    <div className="flex items-center justify-end gap-4">
      {lastSynced && (
        <span className="text-grey-60 text-sm whitespace-nowrap">{getFormattedTimestamp()}</span>
      )}
      <button
        onClick={handleSync}
        disabled={isSyncing}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md border border-grey-30 bg-white text-black-0 text-sm font-medium transition-colors hover:bg-grey-10 disabled:text-grey-60 disabled:cursor-not-allowed`}
        title={isSyncing ? `Processing... ${pollCount > 0 ? `(${pollCount})` : ''}` : 'Process buffered events and update report'}
      >
        {isSyncing ? (
          <svg className="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
        )}
        <span>Sync</span>
      </button>
    </div>
  );
};

export default SyncButtonWithTimestamp; 